import { defineStore } from 'pinia';
import { useUserStore } from '@store/user';
import dayjs from 'dayjs';

export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        user_id: null,
        selected_company_id: null,
        start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
        agents: [],
    }),

    actions: {
        async retrieveAgents() {
            if (this.agents.length === 0) {
                const {
                    data: { data: response },
                } = await window.tenantAxios.get('/users/tenant?limit=9999');
                this.agents = response;
                return response;
            }
            return this.agents;
        },

        async retrieveLeadsUnanswered(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/leads-unanswered`);
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrievePendingApprovals(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/pending-approvals`);
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveSchedulingActions(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/scheduling-actions`);
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveGraph(
            days,
            revenue,
            leads,
            companyId = this.getCompanyId,
            user_id = this.getUserId,
            start_date = this.getStartDate,
            end_date = this.getEndDate,
        ) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/sales-graph`, {
                    params: {
                        days,
                        leads,
                        revenue,
                        user_id,
                        start_date,
                        end_date: end_date ?? start_date,
                    },
                });
                return response.data.data;
            } catch {
                return { revenue: {}, leads: {} };
            }
        },

        async retrieveJobsCount(companyId = this.getCompanyId, status = 'PENDING') {
            try {
                const response = await window.tenantAxios.get(
                    `/companies/${companyId}/stats/jobs-count?status=${status}`,
                );

                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveLeadStats(companyId, type, startDate, endDate, userId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/lead-stats`, {
                    params: {
                        type,
                        start_date: startDate,
                        end_date: endDate,
                        user_id: userId,
                    },
                });
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveSalesLeaderboard(companyId, type, startDate, endDate, sort) {
            try {
                const params = {
                    start_date: startDate,
                    end_date: endDate,
                };
                if (type && type !== 'ALL') {
                    params.type = type;
                }
                if (sort) {
                    params.sort = sort;
                }
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/sales-leaderboard`, {
                    params,
                });
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveRevenueLeadsGraph(companyId, days, startDate, endDate, userId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/revenue-leads-graph`, {
                    params: {
                        days,
                        start_date: startDate,
                        end_date: endDate,
                        user_id: userId,
                    },
                });
                return response.data.data;
            } catch {
                return [];
            }
        },

        async retrieveProductableStats(companyId, startDate, endDate, userId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/productable-stats`, {
                    params: {
                        start_date: startDate,
                        end_date: endDate,
                        user_id: userId,
                    },
                });
                return response.data.data;
            } catch {
                return null;
            }
        },

        async retrieveLeadsCount(companyId = this.getCompanyId, status = 'NEW_LEAD') {
            try {
                const response = await window.tenantAxios.get(
                    `/companies/${companyId}/stats/leads-count?status=${status}`,
                );

                return response.data.data;
            } catch {
                return null;
            }
        },
    },

    getters: {
        getUserId(state) {
            return state.user_id;
        },
        getStartDate(state) {
            return state.start_date;
        },
        getEndDate(state) {
            return state.end_date ?? state.start_date;
        },
        getKeys() {
            return [this.getUserId, this.getStartDate, this.getEndDate, this.getTeam];
        },
        createParams: () => {
            return (params) => {
                let query = '';
                let first = true;
                for (const [key, val] of Object.entries(params)) {
                    if (val) {
                        if (first) {
                            query += `?${key}=${val}`;
                            first = false;
                        } else {
                            query += `&${key}=${val}`;
                        }
                    }
                }
                return query;
            };
        },
        isAdmin(state) {
            const userStore = useUserStore();
            return (
                userStore.isAdmin &&
                (state.user_id && state.agents.length ?
                    state.agents.find((agent) => agent.id == state.user_id)?.roles?.includes('ADMIN')
                :   true)
            );
        },
        getAgents(state) {
            return state.agents ?? [];
        },
        getAvatar(state) {
            return state.agents.find((agent) => agent.id == state.user_id)?.avatar ?? null;
        },
        getTeam(state) {
            return state.agents.find((agent) => agent.id == state.user_id)?.team ?? null;
        },
        getSelectAgent(state) {
            return state.agents.find((agent) => agent.id === state.user_id) ?? null;
        },
        getCompanyId: (state) => {
            const userStore = useUserStore();
            return state.selected_company_id ?? userStore.getCompanyId;
        },
    },
});
